import type { TFunction } from 'i18next';

import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.ts';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { compareUpcomingBirthday } from '@sola/shared-utils/children/utils.ts';
import {
  isCommitmentActive,
  isCommitmentCurrentRecurring,
} from '@sola/shared-utils/supporters/commitments.ts';

export type GroupedCommitments = Record<string, GraphQLCommitment[]>;

/**
 * Gets the sum of the amount due for an array of commitments.
 * @param commitments The array of commitments.
 * @returns The sum of the amount due.
 */
export const getCommitmentsTotalDue = (
  commitments: GraphQLCommitment[],
): number => {
  if (commitments.length === 0) {
    return 0;
  }

  return commitments.reduce(
    (sum, c) => sum + (isCommitmentActive(c) ? c.amountDue || 0 : 0),
    0,
  );
};

export const getCommitmentsByPaymentMethodId = (
  commitments: GraphQLCommitment[],
) => {
  return commitments.reduce<Record<string, GraphQLCommitment[]>>(
    (acc, commitment) => {
      const { paymentMethodId } = commitment;
      if (!paymentMethodId || !isCommitmentCurrentRecurring(commitment)) {
        return acc;
      }

      if (!acc[paymentMethodId]) {
        acc[paymentMethodId] = [];
      }

      acc[paymentMethodId].push(commitment);
      return acc;
    },
    {},
  );
};

export const groupCommitmentsByType = (
  commitments: GraphQLCommitment[],
): GroupedCommitments => {
  const initialValue: GroupedCommitments = {
    [CommitmentTypeEnum.ChildSponsorship]: [],
    [CommitmentTypeEnum.Correspondence]: [],
    [CommitmentTypeEnum.FundCommitment]: [],
    [CommitmentTypeEnum.GiftCommitment]: [],
    [CommitmentTypeEnum.Intervention]: [],
    [CommitmentTypeEnum.GiftCatalog]: [],
    [CommitmentTypeEnum.LivingSponsorship]: [],
    [CommitmentTypeEnum.NA]: [],
  };
  return commitments.reduce((acc, commitment) => {
    if (commitment.commitmentType && commitment.commitmentType in acc) {
      acc[commitment.commitmentType] = [
        ...acc[commitment.commitmentType],
        commitment,
      ];
    }
    return acc;
  }, initialValue);
};

export const groupCommitmentsByFrequency = (
  commitments: GraphQLCommitment[],
): GroupedCommitments => {
  const initialValue: GroupedCommitments = {
    // [PaymentFrequencyType.OneTime]: [],
    [PaymentFrequencyType.Monthly]: [],
    [PaymentFrequencyType.Quarterly]: [],
    [PaymentFrequencyType.Semiannually]: [],
    [PaymentFrequencyType.Annually]: [],
  };

  return commitments.reduce((acc, commitment) => {
    if (commitment.frequency && commitment.frequency in acc) {
      acc[commitment.frequency] = [...acc[commitment.frequency], commitment];
    }

    return acc;
  }, initialValue);
};

export const sortSponsorshipCommitmentsByParticipantName = (
  commitments: GraphQLCommitment[],
): GraphQLCommitment[] =>
  [...commitments].sort((a, b) => {
    const nameA = a.participant?.name?.toLowerCase() ?? '';
    const nameB = b.participant?.name?.toLowerCase() ?? '';

    return nameA.localeCompare(nameB);
  });

export const sortSponsorshipCommitmentsByBirthdate = (
  commitments: GraphQLCommitment[],
): GraphQLCommitment[] =>
  commitments.toSorted((a, b) => {
    const birthdateComparison = compareUpcomingBirthday(
      a.participant?.birthdate,
      b.participant?.birthdate,
    );

    if (birthdateComparison !== 0) {
      return birthdateComparison;
    }

    const nameA = a.participant?.name?.toLowerCase() ?? '';
    const nameB = b.participant?.name?.toLowerCase() ?? '';

    return nameA.localeCompare(nameB);
  });

const getIsFundCode =
  <T extends CommitmentCodeEnum>(enumType: T) =>
  (fundCode: CommitmentCodeEnum): fundCode is T =>
    fundCode === enumType;

export const isFundCodeChildSponsorship = getIsFundCode(
  CommitmentCodeEnum.ChildSponsorship,
);

export const isFundCodeBirthdayGift = getIsFundCode(
  CommitmentCodeEnum.BirthdayGift,
);

export const isFundCodeCenterGift = getIsFundCode(
  CommitmentCodeEnum.CenterGift,
);

export const isFundCodeChildGift = getIsFundCode(CommitmentCodeEnum.ChildGift);

export const isFundCodeChristmasGift = getIsFundCode(
  CommitmentCodeEnum.ChristmasGift,
);

export const isFundCodeFamilyGift = getIsFundCode(
  CommitmentCodeEnum.FamilyGift,
);

/**
 * Format user-friendly commitment title to be used across various UIs.
 */
export const getCommitmentTitle = (
  commitment: GraphQLCommitment,
  t: TFunction<'my', undefined>,
): string => {
  // TODO: Title changes based on type of commitment
  // CDSP will display child's name
  // BG will display "{childName}'s Birthday"
  // Seems like other funds are displaying fund name

  if (commitment.fund?.code) {
    if (
      isFundCodeChildSponsorship(commitment.fund.code as CommitmentCodeEnum)
    ) {
      return commitment.participant?.name || '';
    }

    if (isFundCodeBirthdayGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.birthday', {
        childName: commitment.participant?.name,
      });
    }

    if (isFundCodeChildGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.child', {
        childName: commitment.participant?.name,
      });
    }

    if (isFundCodeFamilyGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.family', {
        childName: commitment.participant?.name,
      });
    }

    if (isFundCodeCenterGift(commitment.fund.code as CommitmentCodeEnum)) {
      return t('commitments.title.center');
    }
  }
  // Bad data guard: fall back to participant name only for child-related commitment types
  if (
    commitment.commitmentType === GraphQLCommitmentType.ChildSponsorship ||
    commitment.commitmentType === GraphQLCommitmentType.LivingSponsorship
  ) {
    return commitment.participant?.name || commitment.fund?.description || '';
  }

  return commitment.fund?.description || '';
};

/**
 * Get commitment label for commitment type to be used across various UIs.
 */
export const getCommitmentLabel = (
  commitment: GraphQLCommitment,
  t: TFunction<'my', undefined>,
) => {
  if (isFundCodeChristmasGift(commitment.fund?.code as CommitmentCodeEnum)) {
    return t('commitments.category.fundCommitment');
  }

  switch (commitment.commitmentType) {
    case GraphQLCommitmentType.ChildSponsorship: {
      return t('commitments.category.childSponsorship');
    }
    case GraphQLCommitmentType.LivingSponsorship: {
      return t('commitments.category.livingSponsorship');
    }
    // TODO: Make sure when integration with live data that this is needed or if the GiftCatalog is all we need
    case GraphQLCommitmentType.Intervention: {
      return t('commitments.category.intervention');
    }
    case GraphQLCommitmentType.FundCommitment: {
      return t('commitments.category.fundCommitment');
    }
    case GraphQLCommitmentType.GiftCommitment: {
      return t('commitments.category.giftCommitment');
    }
    case GraphQLCommitmentType.GiftCatalog: {
      return t('commitments.category.fundCommitment');
    }
    default: {
      return '';
    }
  }
};

/**
 * Get abbreviation for commitment frequency to be used across various UIs.
 */
export const getFrequencyAbbr = (
  frequency: string,
  t: TFunction<'my', undefined>,
) => {
  switch (frequency) {
    case PaymentFrequencyType.Monthly: {
      return t('core:frequencies.abbreviated.monthly');
    }
    case PaymentFrequencyType.Quarterly: {
      return t('core:frequencies.abbreviated.quarterly');
    }
    case PaymentFrequencyType.Semiannually: {
      return t('core:frequencies.abbreviated.semiannually');
    }
    case PaymentFrequencyType.Annually: {
      return t('core:frequencies.abbreviated.annually');
    }
    default: {
      return '';
    }
  }
};

/**
 * Get recurring commitments from a list of commitments.
 */
export const getRecurringCommitments = (commitments: GraphQLCommitment[]) =>
  commitments?.length
    ? commitments.filter(isCommitmentCurrentRecurring).sort((a, b) => {
        if (
          a.commitmentType === CommitmentTypeEnum.ChildSponsorship &&
          b.commitmentType !== CommitmentTypeEnum.ChildSponsorship
        ) {
          return -1;
        }
        if (
          a.commitmentType !== CommitmentTypeEnum.ChildSponsorship &&
          b.commitmentType === CommitmentTypeEnum.ChildSponsorship
        ) {
          return 1;
        }
        return 0;
      })
    : null;

/**
 * Get commitments that are not on a recurring payment plan from a list of commitments.
 */
export const getNonRecurringCommitments = (commitments: GraphQLCommitment[]) =>
  commitments?.length
    ? commitments.filter(
        (commitment) =>
          isCommitmentActive(commitment) &&
          !commitment.isRecurring &&
          commitment.commitmentType !== CommitmentTypeEnum.Correspondence,
      )
    : null;

const getSortCommitmentsByTitle =
  (t: TFunction<'my', undefined>) =>
  (a: GraphQLCommitment, b: GraphQLCommitment) =>
    getCommitmentTitle(a, t)?.localeCompare(getCommitmentTitle(b, t));

/**
  Sort commitments sponsorships, gifts, and then everything else
 */
export const sortCommitmentsByPriority = (
  commitments: GraphQLCommitment[],
  t?: TFunction<'my', undefined>,
) => {
  const {
    [CommitmentTypeEnum.ChildSponsorship]: childSponsorships,
    [CommitmentTypeEnum.GiftCommitment]: giftCommitments,
    ...remainingCommitmentTypes
  } = groupCommitmentsByType(commitments);

  const remainingCommitments = Object.values(remainingCommitmentTypes).flat();

  if (!t) {
    return [...childSponsorships, ...giftCommitments, ...remainingCommitments];
  }

  const sortCommitmentsByTitle = getSortCommitmentsByTitle(t);

  return [
    ...childSponsorships.toSorted(sortCommitmentsByTitle),
    ...giftCommitments.toSorted(sortCommitmentsByTitle),
    ...remainingCommitments.toSorted(sortCommitmentsByTitle),
  ];
};
